import { createAsyncThunk } from "@reduxjs/toolkit";
import { certificatesApi } from "../../api/certificatesApi";
import { CertificateItem } from "../../types/certificates";
import { AppDispatch, RootState } from "../store";
import { CertificateUploadData } from "../../components/common/table/UploadBtn";
import { CertificateDocumentDeleteData } from "../../pages/documentManagement/Certificates/DocumentPopUp/PopUpTableRow";
import { setEditMode } from "../reducers/tableManagementReducer";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";

export const fetchCertificatesThunk = createAsyncThunk<
  CertificateItem[] | null,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "certificates/fetchCertificates",
  async (_, { dispatch }) => {
    const res = await handleRequest<CertificateItem[]>(
      dispatch,
      "indirect",
      () => certificatesApi.getAllCertificates()
    );
    return res.success() ? res.result : null;
  }
);

export const editCertificatesThunk = createAsyncThunk<
  CertificateItem[] | null,
  void,
  { dispatch: AppDispatch; state: RootState }
>(
  "certificates/editCertificates",
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const certificatesDataToEdit = state.certificates.certificatesToEdit;
    const results = await Promise.all(
      certificatesDataToEdit.map(cert =>
        handleRequest<CertificateItem>(
          dispatch,
          "direct",
          () =>
            certificatesApi.editCertificate(cert.id!, {
              documentName: cert.documentName,
              employeeId: cert.employeeId,
              endDate: cert.endDate,
              notificationDate: cert.notificationDate,
            }),
          i18n.t("requestMessage.certificateUpdated")
        )
      )
    );
    const errorResult = results.find(r => !r.success());
    if (errorResult) throw new Error(errorResult.errorMessage ?? "Update failed");
    await dispatch(fetchCertificatesThunk());
    return results.map(r => r.result!).filter(Boolean);
  }
);

export const createNewCertificateThunk = createAsyncThunk<
  CertificateItem | null,
  CertificateItem,
  { dispatch: AppDispatch; state: RootState }
>(
  "certificates/createCertificate",
  async (certificate, { dispatch }) => {
    const newCertificate = {
      documentName: certificate.documentName,
      employeeId: certificate.employeeId,
      endDate: certificate.endDate,
      notificationDate: certificate.notificationDate,
    };
    const res = await handleRequest<CertificateItem>(
      dispatch,
      "direct",
      () => certificatesApi.createCertificate(newCertificate),
      i18n.t("requestMessage.certificateCreated")
    );
    if (res.success()) {
      await dispatch(fetchCertificatesThunk());
      return res.result;
    }
    dispatch(setEditMode(false));
    return null;
  }
);

export const deleteCertificateThunk = createAsyncThunk<
  number | null,
  number,
  { dispatch: AppDispatch; state: RootState }
>(
  "certificates/deleteCertificate",
  async (certificateId, { dispatch }) => {
    const res = await handleRequest<void>(
      dispatch,
      "direct",
      () => certificatesApi.deleteCertificate(certificateId),
      i18n.t("requestMessage.certificateDeleted")
    );
    if (res.success()) {
      await dispatch(fetchCertificatesThunk());
      return certificateId;
    }
    return null;
  }
);

export const certificateFileUploadThunk = createAsyncThunk<
  boolean,
  CertificateUploadData,
  { dispatch: AppDispatch; state: RootState }
>(
  "certificates/uploadFile",
  async (fileDataToSend, { dispatch }) => {
    const res = await handleRequest<void>(
      dispatch,
      "direct",
      () => certificatesApi.uploadCertificateFile(fileDataToSend),
      i18n.t("requestMessage.fileUploaded")
    );
    if (res.success()) {
      await dispatch(fetchCertificatesThunk());
      return true;
    }
    return false;
  }
);


export const deleteCertificateDocumentThunk = createAsyncThunk<
  boolean,
  CertificateDocumentDeleteData,
  { dispatch: AppDispatch; state: RootState }
>(
  "certificates/deleteCertificateDocument",
  async (deleteData, { dispatch }) => {
    const res = await handleRequest<void>(
      dispatch,
      "direct",
      () =>
        certificatesApi.deleteCertificateFile(
          deleteData.documentExchangeWalletId,
          deleteData.documentId
        ),
      i18n.t("requestMessage.documentDeleted")
    );
    if (res.success()) {
      await dispatch(fetchCertificatesThunk());
      return true;
    }
    return false;
  }
);
